import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    CheckCircleOutline,
    CleaningServices,
    Group,
    EmojiPeople,
    Home,
    FormatListNumbered,
    ArrowForwardIos,
    LocalLaundryService,
    Kitchen,
    Hotel,
    AssignmentTurnedIn,
    ThumbUpAlt,
    AccessTime,
    Paid,
    EmojiEmotions
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'はじめに ― 家事が苦手でもルームシェアは楽しめる！' },
    { id: 'idea', label: '家事をラクにするアイデア集' },
    { id: 'sharing', label: '家事分担の工夫とポイント' },
    { id: 'service', label: '家事代行サービスの活用法' },
    { id: 'agreement', label: '納得する分担ルールの作り方' },
    { id: 'faq', label: 'よくある質問・トラブル対策' },
    { id: 'summary', label: 'まとめ ― ルームシェアで快適な家事ライフを' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            minWidth: 30,
                                            minHeight: 30,
                                            bgcolor: '#f5f5f5',
                                            color: '#666',
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1
                                        }}
                                    >
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに ― 家事が苦手でもルームシェアは楽しめる！
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアに興味はあるけれど、「家事が苦手だから心配…」と一歩踏み出せない方は意外と多いものです。
                    でも、安心してください！<Chip label="家事が苦手" color="primary" variant="outlined" size="small" sx={{ mx: 1 }} />な人こそ、
                    ルームシェアの魅力を感じやすいとも言えるのです。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    本記事では、<b>家事が得意でない方</b>がルームシェアで快適に暮らすための工夫や、分担方法、最新の家事代行サービスの活用法まで、具体的なノウハウをたっぷりご紹介します。
                </Typography>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/share-living-dining_roomshare-kaji-nigate-kotsu.jpg"
                        alt="明るい共有リビングダイニングで、ルームメイト同士が和やかに家事について相談している様子。木目調のテーブルと観葉植物が温かみを演出し、全体に柔らかな自然光が差し込む。"
                        style={{ width: '100%', borderRadius: 12, marginBottom: 8 }}
                    />
                </Box>
                <Typography variant="body2" color="textSecondary">
                    この記事を読めば、<b>家事ストレスを最小限にしつつ、ルームシェア生活をもっと楽しむヒント</b>が見つかります。ぜひ、最後までご覧ください！
                </Typography>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* 家事をラクにするアイデア集 */}
            <Box id="idea" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家事をラクにするアイデア集
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    まずは、<b>「家事が苦手」な人でも無理なく続けられるコツ</b>をご紹介します。「完璧」を目指さず、ちょっとした工夫で日々の負担をぐっと減らしましょう。
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <LocalLaundryService color="primary" />
                            </ListItemIcon>
                            <Box>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>洗濯は「まとめて」「効率重視」</Typography>
                                <Typography variant="body2">
                                    洗濯物は各自まとめて週2回などにし、乾燥機やランドリーサービスも積極活用。手間の削減がコツです。
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <Kitchen color="primary" />
                            </ListItemIcon>
                            <Box>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>キッチンは「自分ルール」でOK</Typography>
                                <Typography variant="body2">
                                    料理が苦手でも、電子レンジ調理やお惣菜を活用。<b>「使ったら元に戻す」</b>だけは守れば十分です。
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <CleaningServices color="primary" />
                            </ListItemIcon>
                            <Box>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>掃除は「ついで掃除」&「分担」</Typography>
                                <Typography variant="body2">
                                    気づいたときにサッと掃除。ロボット掃除機やウェットシートも大活躍。負担が分散されます。
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <Hotel color="primary" />
                            </ListItemIcon>
                            <Box>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>ベッドメイキングは「自分のタイミングで」</Typography>
                                <Typography variant="body2">
                                    「朝が苦手」でもOK。自分のペースで気楽に、シーツ交換は月1回でも清潔に保てます。
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>

                </Box>
                <Typography variant="body2" color="textSecondary">
                    家事を「義務」ではなく、<b>生活の中の“ちょっとした工夫”</b>として捉えるとストレスが減ります。
                </Typography>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* 家事分担の工夫とポイント */}
            <Box id="sharing" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家事分担の工夫とポイント
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアで揉めがちな<strong>家事分担</strong>。でも、ちょっとした工夫でうまく回せます。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="h6" sx={{ mb: 1, fontSize: '1.03rem' }}>
                        <AssignmentTurnedIn sx={{ mr: 1, color: '#1976d2' }} />
                        分担ルールの定番パターン
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><FormatListNumbered color="primary" /></ListItemIcon>
                            <ListItemText primary="当番制（週替わり・日替わり）" secondary="公平かつ、忘れにくい。体調不良時は交代もOK。" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><Group color="primary" /></ListItemIcon>
                            <ListItemText primary="タスクごと担当制" secondary="料理はAさん、掃除はBさんなど、得意分野を活かせる。" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><EmojiPeople color="primary" /></ListItemIcon>
                            <ListItemText primary="やれる人がやる「ゆる分担」" secondary="忙しい時期・不得意な家事は柔軟にサポートし合う。" />
                        </ListItem>
                    </List>
                </Paper>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <b>ポイントは「見える化」と「柔軟さ」</b>。ホワイトボードやアプリで当番を可視化し、「できない時は代わってもらう」のもアリです。
                </Typography>
                <Box sx={{ my: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ maxWidth: 345, p: 1, borderRadius: 2, mb: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
                                        <ThumbUpAlt color="primary" sx={{ mr: 1 }} />
                                        分担をうまく回すコツ
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                            <ListItemText primary="得意・不得意を事前に共有" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                            <ListItemText primary="家事内容を細分化してリスト化" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                            <ListItemText primary="定期的に振り返りミーティング" />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Card sx={{ maxWidth: 345, p: 1, borderRadius: 2, mb: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
                                        <AccessTime color="primary" sx={{ mr: 1 }} />
                                        時間を節約する分担例
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                            <ListItemText primary="朝掃除・夜片付けを分担" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                            <ListItemText primary="「ながら家事」を推奨" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                            <ListItemText primary="週末まとめて作業デーを設定" />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
                <Typography variant="body2" color="textSecondary">
                    「できることをできる人がやる」精神でいれば、ストレスも軽減。<b>「ありがとう」が行き交う関係</b>が家事分担成功のカギです。
                </Typography>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* 家事代行サービスの活用法 */}
            <Box id="service" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    家事代行サービスの活用法
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「どうしても家事が苦手…」<Chip label="プロの手を借りる" color="primary" variant="outlined" size="small" sx={{ mx: 1 }} />のも大いにアリ！
                    最近は、<b>ルームシェア向け家事代行サービス</b>も増えています。
                </Typography>
                <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: '#f1f8e9' }}>
                    <Typography variant="h6" sx={{ fontSize: '1.03rem', mb: 1 }}>
                        <Paid sx={{ mr: 1, color: '#43a047' }} />
                        家事代行サービスのメリット
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><ThumbUpAlt color="primary" /></ListItemIcon>
                            <ListItemText primary="プロによる徹底的な清掃で共有スペースが快適に" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ThumbUpAlt color="primary" /></ListItemIcon>
                            <ListItemText primary="家事ストレスから解放される" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ThumbUpAlt color="primary" /></ListItemIcon>
                            <ListItemText primary="みんなで割り勘すれば意外と安い" />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ my: 2 }}>

                </Box>
                <Typography variant="h6" sx={{ fontSize: '1.09rem', mt: 2, mb: 1 }}>
                    家事代行の選び方・依頼のポイント
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIos color="action" sx={{ fontSize: 16, mr: 0.5 }} /></ListItemIcon>
                        <ListItemText primary="「定期コース」だと割安" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIos color="action" sx={{ fontSize: 16, mr: 0.5 }} /></ListItemIcon>
                        <ListItemText primary="掃除・料理・洗濯など希望内容を明確に伝える" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIos color="action" sx={{ fontSize: 16, mr: 0.5 }} /></ListItemIcon>
                        <ListItemText primary="家に入れる前の身元確認・口コミチェックも大事" />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><ArrowForwardIos color="action" sx={{ fontSize: 16, mr: 0.5 }} /></ListItemIcon>
                        <ListItemText primary="費用分担はルームメイトで事前に合意" />
                    </ListItem>
                </List>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    <b>プロの力</b>を借りることで、みんなの生活満足度がグッとアップします。
                </Typography>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* 納得する分担ルールの作り方 */}
            <Box id="agreement" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    納得する分担ルールの作り方
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでは「<b>納得感</b>」が何より大切。<Chip label="フェアなルール" color="primary" variant="outlined" size="small" sx={{ mx: 1 }} />で、みんなが気持ちよく過ごせます。
                </Typography>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: '#e1f5fe' }}>
                    <Typography variant="h6" sx={{ fontSize: '1.04rem', mb: 1 }}>
                        ルール作りのステップ
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><ArrowForwardIos color="primary" sx={{ fontSize: 16, mr: 0.5 }} /></ListItemIcon>
                            <ListItemText primary="家事の範囲・頻度・分担を全員で明確に決める" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ArrowForwardIos color="primary" sx={{ fontSize: 16, mr: 0.5 }} /></ListItemIcon>
                            <ListItemText primary="苦手分野・事情があれば遠慮せず相談" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ArrowForwardIos color="primary" sx={{ fontSize: 16, mr: 0.5 }} /></ListItemIcon>
                            <ListItemText primary="分担表やLINEグループで「見える化」" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ArrowForwardIos color="primary" sx={{ fontSize: 16, mr: 0.5 }} /></ListItemIcon>
                            <ListItemText primary="月1回は“振り返り”タイム（改善案の話し合い）" />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ my: 2 }}>

                </Box>
                <Typography variant="body2" color="textSecondary">
                    <b>「完璧」より「納得できる形」を目指す</b>ことが、長く続く秘訣です。
                </Typography>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* よくある質問・トラブル対策 */}
            <Box id="faq" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    よくある質問・トラブル対策
                </Typography>
                <List>
                    <ListItem alignItems="flex-start">
                        <ListItemIcon><EmojiEmotions color="primary" /></ListItemIcon>
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                Q. ルームメイトが家事を全然やらなくて困ります…
                            </Typography>
                            <Typography variant="body2">
                                まずは「なぜできないのか」理由を聞いてみましょう。忙しさや体調、単純な忘れなど、話してみると案外解決策が見つかることも。<b>責めずに相談</b>が大事です。
                            </Typography>
                        </Box>
                    </ListItem>
                    <Divider sx={{ my: 1 }} />
                    <ListItem alignItems="flex-start">
                        <ListItemIcon><EmojiEmotions color="primary" /></ListItemIcon>
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                Q. 苦手な家事だけ免除してもらうのはアリ？
                            </Typography>
                            <Typography variant="body2">
                                もちろんOK！その分、別の作業や雑務を担当するなど、<b>全体のバランス</b>を取れば問題ありません。
                            </Typography>
                        </Box>
                    </ListItem>
                    <Divider sx={{ my: 1 }} />
                    <ListItem alignItems="flex-start">
                        <ListItemIcon><EmojiEmotions color="primary" /></ListItemIcon>
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                Q. 家事代行の費用分担はどうすれば？
                            </Typography>
                            <Typography variant="body2">
                                人数割りが基本ですが、利用頻度や希望内容によって柔軟に話し合いましょう。<b>最初に合意</b>しておけばトラブル防止に。
                            </Typography>
                        </Box>
                    </ListItem>
                </List>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 7 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ ― ルームシェアで快適な家事ライフを
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    家事が苦手でも、<b>分担の工夫</b>や<b>家事代行サービスの活用</b>で、ルームシェア生活はもっと自由で快適になります。
                    一番大切なのは「<b>みんなで納得できる仕組み</b>」を作ること。無理せず、できる範囲で助け合いを楽しみましょう。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    この記事を参考に、ぜひ自分たちなりの<b>「ラク家事ルームシェア」</b>を実現してください！
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-kaji-nigate-kotsu',
    slug: 'roomshare-kaji-nigate-kotsu',
    title: 'ルームシェアで「家事が苦手」な人が快適に暮らすコツ',
    excerpt: '家事をラクにするアイデア 分担の工夫 家事代行サービス活用 みんなが納得する方法',
    coverImage: '/images/blog/cover-modern-share-living_roomshare-kaji-nigate-kotsu.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。ナチュラルな木目調の内装とカラフルなインテリアが調和し、安心感と活気ある雰囲気が伝わる。',
    publishedAt: '2024-06-12T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '暮らし', '家事'],
    tags: ['家事分担', '家事代行', 'ルームシェアのコツ'],
    author: {
        name: '三浦 遥',
        avatar: '/images/avatars/miura-haruka.jpg',
        bio: '住宅・共同生活の実践取材を重ねるライター。自身もシェアハウス経験者。快適なルームシェア運営・家事分担ノウハウを多くのメディアで発信中。',
        profilePicture: '/images/avatars/miura-haruka.jpg'
    },
    viewCount: 1000,
    contentComponent: PostContent
};

export default post;